import { GuideCoursePage, GuideProductPage } from '@course/components/guide-course-page';
import { rpx } from 'client/lib/rpx-client';
import { LoadedProps, RouteLoadProps } from '@components/router';
import { defCoursesRoute } from '@course/components/courses-app-router';
import { CommsAgenda } from '@components/comms-agenda';
import { ScheduleViewToggle, useScheduleView } from '@components/comms-agenda/view-toggle';
import { upcomingDateRange } from 'shared/comms-schedule';
import { MessagesNavBar } from './messages-nav-bar';
import { useState } from 'preact/hooks';
import { messagesUrl } from './urls';
import { DEFAULT_CONTENT, Message } from '@components/messages';
import { showError } from '@components/app-error';
import { BtnPrimary } from '@components/buttons';
import { IcoPlus } from '@components/icons';

const sysStore = rpx.systemMessages;
const store = rpx.messages;

export const route = defCoursesRoute({
  load,
  authLevel: 'guide',
  Page,
});

async function load(route: RouteLoadProps) {
  const { courseId } = route.params;
  const { startDate, endDate } = upcomingDateRange();

  const [course, items, systemMessages, customMessages] = await Promise.all([
    rpx.courses.getGuideCourse({ id: courseId }),
    rpx.commsSchedule.getCommsSchedule({ courseId, startDate, endDate }),
    sysStore.getSystemMessages({ courseId }),
    store.getMessages({ courseId }),
  ]);

  const messages: Message[] = [];

  systemMessages.forEach((m) => {
    let subject = m.title;
    if (course.isProduct && m.type !== 'welcome') {
      return;
    }
    if (m.type === 'welcome') {
      subject = 'Welcome';
    } else if (m.type === 'moduleStart') {
      subject = 'Start of Module';
    } else if (m.type === 'invitation') {
      subject = 'Invitation';
    } else if (m.type === 'deferredActivation') {
      subject = 'Access Opens';
    }
    messages.push({
      id: m.type,
      subject,
      isSystem: true,
      updatedAt: m.isCustomized ? m.updatedAt : undefined,
      editUrl: messagesUrl(course, m.type),
    });
  });

  customMessages.forEach((m) => {
    messages.push({
      id: m.id,
      subject: m.title,
      isSystem: false,
      updatedAt: m.updatedAt,
      sentAt: m.sentAt,
      editUrl: messagesUrl(course, m.id),
      startDate: m.startDate,
      startOffset: course.accessFormat === 'ondemand' ? m.startOffset : undefined,
    });
  });

  return { course, items, messages, startDate, endDate };
}

function Content(props: LoadedProps<typeof load>) {
  const { course } = props.state;
  const [messages, setMessages] = useState(props.state.messages);
  const router = props.router;
  const { view, setView, items, isLoading } = useScheduleView({
    initialItems: props.state.items,
    fetch: (range) => rpx.commsSchedule.getCommsSchedule({ courseId: course.id, ...range }),
  });

  const onCreate = async () => {
    try {
      const result = await store.saveMessage({
        title: '',
        content: DEFAULT_CONTENT,
        courseId: course.id,
      });
      const msg: Message = {
        id: result.id,
        editUrl: messagesUrl(course, result.id),
        subject: result.title,
        updatedAt: result.updatedAt,
      };
      setMessages((msgs) => [msg, ...msgs]);
      router.goto(msg.editUrl);
    } catch (err) {
      showError(err);
    }
  };

  return (
    <section class="flex grow flex-col md:flex-row">
      <MessagesNavBar
        course={course}
        selectedId="schedule"
        messages={messages}
        onCreate={(msg) => setMessages((msgs) => [msg, ...msgs])}
      />
      <div class="p-4 md:p-8 md:pb-16 grow">
        <header class="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 class="text-2xl font-semibold">Email schedule</h1>
            <p class="text-sm text-gray-500 mt-1">
              Scheduled messages and meeting reminders for this course.
            </p>
          </div>
          <BtnPrimary onClick={onCreate}>
            <IcoPlus class="w-4 h-4" /> New message
          </BtnPrimary>
        </header>
        <div class="mb-4">
          <ScheduleViewToggle view={view} onChange={setView} />
        </div>
        <CommsAgenda
          items={items}
          showCourseColumn={false}
          view={view}
          emptyState={
            isLoading ? (
              'Loading…'
            ) : view === 'sent' ? (
              <div class="flex flex-col gap-2">
                <span class="text-gray-700 font-medium">No sends in the past 6 months.</span>
              </div>
            ) : (
              <div class="flex flex-col gap-2">
                <span class="text-gray-700 font-medium">Nothing scheduled.</span>
                <span class="text-sm">
                  Compose a message or enable meeting reminders to see them here.
                </span>
              </div>
            )
          }
        />
      </div>
    </section>
  );
}

function Page(props: LoadedProps<typeof load>) {
  const { course } = props.state;

  if (course.isProduct) {
    return (
      <GuideProductPage page="messages" product={course}>
        <Content {...props} />
      </GuideProductPage>
    );
  }

  return (
    <GuideCoursePage course={course} documentTitle="Email schedule" type="messages">
      <Content {...props} />
    </GuideCoursePage>
  );
}
